import { Button } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AtlasGqlHorizonDamageObservationGroup } from 'types/atlas-graphql';

import { EditAttributesForm } from './EditAttributesForm';
import { StyledModal } from './EditAttributesModal.style';

const DAMAGE_ATTRIBUTES_FORM_ID = 'create-new-damage-attributes-form';

type EditAttributesModalProps = {
  open: boolean;
  damageId: string;
  selectedSchema?: string;
  observationGroup: AtlasGqlHorizonDamageObservationGroup;
  onCancel: () => void;
};

export const EditAttributesModal: React.FunctionComponent<EditAttributesModalProps> = ({
  open,
  damageId,
  selectedSchema,
  observationGroup,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);

  // This state lives above `destroyOnHidden`, so it survives the form being torn down. Reset it on
  // close so a reopened modal doesn't start with the previous session's Save button state.
  useEffect(() => {
    if (!open) {
      setSaving(false);
      setSaveDisabled(true);
    }
  }, [open]);

  return (
    <StyledModal
      open={open}
      title={t('damage.editDamageAttributes', 'Edit Damage Attributes')}
      width={900}
      onCancel={onCancel}
      destroyOnHidden
      footer={[
        <Button key="cancel" data-testid="damage-modal-cancelBtn" onClick={onCancel}>
          {t('common.cancel', 'Cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          htmlType="submit"
          form={DAMAGE_ATTRIBUTES_FORM_ID}
          loading={saving}
          disabled={saveDisabled}
          data-testid="damage-modal-saveBtn"
        >
          {t('common.save', 'Save')}
        </Button>,
      ]}
    >
      <EditAttributesForm
        damageId={damageId}
        selectedSchema={selectedSchema}
        observationGroup={observationGroup}
        onCancel={onCancel}
        onSavingChange={setSaving}
        onSaveDisabledChange={setSaveDisabled}
        showLoadingSpinner={true}
        attributeLayout={{ number: 2, threshold: 5 }}
        formLayout="horizontal"
      />
    </StyledModal>
  );
};
